import { Edit3, History, ArrowUp, ArrowDown, ArrowUpDown, Star } from 'lucide-react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { useTableSort } from '../../../hooks/useTableSort';
import DirectProductStar from '../shared/DirectProductStar';
import { isNegativeStockHealth } from '../shared/stockHealth';
import { rowKey } from './allSuppliers';
import RemarkCell from './RemarkCell';
import { COLUMN_HINTS, isNegativeShortage, NEGATIVE_SHORTAGE_CLASS } from './shortageColumns';
import type { StockPlanningItem, ColumnPreference } from '../../../lib/api';

interface StockPlanningTableProps {
  items: StockPlanningItem[];
  columns: ColumnPreference[];
  /**
   * When supplied, an extra Actions column renders with a "Propose change"
   * button per row. Used by the supplier-side grid; left undefined for the
   * customer view so no column is shown.
   */
  onProposeChange?: (item: StockPlanningItem) => void;
  /**
   * Optional. When supplied alongside onProposeChange, a "History" button
   * is rendered next to "Propose" so the supplier can open the part's
   * change-request history without leaving the grid.
   */
  onViewHistory?: (item: StockPlanningItem) => void;
  /**
   * CR-6176. When supplied, the part number renders as a link that opens the
   * PO Lines view filtered to that part (all purchase orders for the part).
   */
  onPartNumberClick?: (partNumber: string) => void;
  /**
   * CR-6180. Supplier grid only. When true, "Ordered Overall"
   * (ordered_by_supplier) renders as a greyed 0 once the order is complete —
   * i.e. the supplier's uploaded stock fully covers it (remaining_to_produce
   * ≤ 0). Display-only: the underlying value (which drives Fulfilment %) is
   * untouched. The customer grid leaves this off so its column is unaffected.
   */
  zeroOrderedWhenProduced?: boolean;
  /**
   * CR-8613. When supplied, the customer ticks Direct in the Part Number cell —
   * the same flag as the Parts List's Direct column. Left undefined (the
   * supplier's grid) only the star shows.
   */
  onToggleDirect?: (item: StockPlanningItem, value: boolean) => void;
  /** CR-8613: part ids whose Direct change is still saving. */
  savingDirectIds?: ReadonlySet<number>;
  /**
   * CR-8624. When supplied (a customer admin), the Remarks cell can be edited;
   * it resolves true once saved. Left undefined, remarks are read-only.
   */
  onSaveRemark?: (item: StockPlanningItem, text: string) => Promise<boolean>;
  /** CR-8624: whether a row's remark is still saving. */
  isRemarkSaving?: (item: StockPlanningItem) => boolean;
}

const SORT_HINT = 'Click to sort • double-click to bring negative values to the top';

/** CR-8624: remarks run to a sentence ("on order, arriving wc 12th Oct"). */
const REMARKS_WIDTH = 220;

/** Columns that should be frozen (sticky) on the left when scrolling horizontally.
 *  CR-8193: Supplier leads on the all-suppliers grid. */
const FROZEN_COLUMNS = ['supplier_name', 'part_number', 'description'];

export default function StockPlanningTable({
  items, columns, onProposeChange, onViewHistory, onPartNumberClick, zeroOrderedWhenProduced,
  onToggleDirect, savingDirectIds, onSaveRemark, isRemarkSaving,
}: StockPlanningTableProps) {
  // CR-6180: an order is "complete" for the supplier once their uploaded stock
  // fully covers it (remaining_to_produce ≤ 0 while something was ordered).
  const orderedComplete = (item: StockPlanningItem): boolean => {
    if (!zeroOrderedWhenProduced) return false;
    const ordered = parseFloat(item.ordered_by_supplier) || 0;
    const remaining = parseFloat(item.remaining_to_produce) || 0;
    return ordered > 0 && remaining <= 0;
  };

  // CR-6296: spreadsheet-style client-side sort of the loaded rows. Staff want
  // negative (deficit) values at the top of health columns since those parts
  // need calling off first. Single-click a header to sort (numeric high→low,
  // text A–Z; click again to flip); double-click to jump straight to
  // "negatives first". Shared with the warehouse wizard via useTableSort.
  const {
    sortColumn, sortDirection, sortedItems,
    handleHeaderClick, handleHeaderDoubleClick, getAriaSort,
  } = useTableSort<StockPlanningItem>(items);

  const renderSortIcon = (columnId: string) => {
    if (sortColumn !== columnId) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50 shrink-0" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary shrink-0" />
      : <ArrowDown className="h-3 w-3 text-primary shrink-0" />;
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No stock planning data available.
      </div>
    );
  }

  const getCellValue = (item: StockPlanningItem, columnId: string): string => {
    const value = item[columnId as keyof StockPlanningItem];
    if (value === undefined || value === null) return '-';
    // CR-8624: a part with no Commodity Type in the Parts Master.
    if (columnId === 'commodity_type' && value === '') return '—';
    // CR-6180: on the supplier grid, Remaining to Produce shows 0 (not blank)
    // when complete, matching the "Overall Ordered" column. Other views keep
    // the original blank-at-≤0 behaviour.
    if (columnId === 'remaining_to_produce') {
      const num = parseFloat(String(value));
      if (!isNaN(num) && num <= 0) return zeroOrderedWhenProduced ? '0' : '';
    }
    return String(value);
  };

  const renderFulfilmentBar = (raw: string) => {
    const pct = parseFloat(raw);
    if (isNaN(pct)) return <span className="text-muted-foreground">-</span>;
    const clamped = Math.max(0, Math.min(100, pct));
    const barColor = pct >= 100 ? 'bg-emerald-500' : pct >= 75 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="flex items-center gap-2 min-w-[120px]">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${clamped}%` }} />
        </div>
        <span className="text-xs font-medium tabular-nums whitespace-nowrap">{pct.toFixed(0)}%</span>
      </div>
    );
  };

  const getCellClassName = (columnId: string, value: string): string => {
    if (columnId === 'directs_covered') {
      return value === 'YES' ? 'text-green-600 font-medium' : 'text-red-600 font-medium';
    }
    if (columnId === 'current_shortage' || columnId === 'stock_health_min') {
      const num = parseFloat(value);
      if (!isNaN(num) && num < 0) return 'text-red-600';
      if (!isNaN(num) && num > 0) return 'text-green-600';
    }
    // CR-7717: Stock Health Max goes red when negative too.
    if (isNegativeStockHealth(columnId, value)) return 'text-red-600';
    // CR-8624: a shortage below 0 is shaded, as the out-of-stock sheet shades it.
    if (isNegativeShortage(columnId, value)) return NEGATIVE_SHORTAGE_CLASS;
    return '';
  };

  // Calculate left offsets for frozen columns
  const FROZEN_WIDTHS: Record<string, number> = {
    supplier_name: 160,
    part_number: 130,
    description: 160,
  };

  const getFrozenLeft = (columnId: string): number => {
    let left = 0;
    for (const frozenId of FROZEN_COLUMNS) {
      if (frozenId === columnId) break;
      // Only accumulate if this frozen column is actually visible
      if (columns.some(c => c.column_id === frozenId)) {
        left += FROZEN_WIDTHS[frozenId] ?? 120;
      }
    }
    return left;
  };

  const isFrozen = (columnId: string): boolean => FROZEN_COLUMNS.includes(columnId);

  /** CR-8613: the Direct tick in the Part Number cell (customer only). */
  const renderDirectTick = (item: StockPlanningItem) => onToggleDirect && (
    <Checkbox
      aria-label={`Direct product: ${item.part_number}`}
      title={item.is_direct_product
        ? 'Direct product — untick to remove it here and on the Parts List'
        : 'Tick to mark this part Direct — the same tick as the Parts List'}
      checked={Boolean(item.is_direct_product)}
      disabled={savingDirectIds?.has(item.id)}
      onCheckedChange={(v) => onToggleDirect(item, v === true)}
      className="mr-1.5 align-text-bottom"
    />
  );

  return (
    <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-340px)] border rounded-lg">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-20">
          <tr className="border-b bg-muted/80 backdrop-blur-sm">
            {columns.map((col) => {
              const frozen = isFrozen(col.column_id);
              const active = sortColumn === col.column_id;
              return (
                <th
                  key={col.column_id}
                  onClick={() => handleHeaderClick(col.column_id)}
                  onDoubleClick={() => handleHeaderDoubleClick(col.column_id)}
                  title={COLUMN_HINTS[col.column_id] ? `${COLUMN_HINTS[col.column_id]}\n\n${SORT_HINT}` : SORT_HINT}
                  aria-sort={getAriaSort(col.column_id)}
                  className={`whitespace-normal break-words align-top text-xs text-left font-medium px-3 py-2 cursor-pointer select-none hover:bg-muted ${
                    active ? 'bg-muted' : ''
                  } ${
                    frozen ? 'sticky z-30 bg-muted shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''
                  }`}
                  style={frozen ? {
                    left: getFrozenLeft(col.column_id),
                    minWidth: FROZEN_WIDTHS[col.column_id] ?? 120,
                  } : { minWidth: col.column_id === 'remarks' ? REMARKS_WIDTH : 100 }}
                >
                  <div className="flex items-start justify-between gap-1">
                    <span className="inline-flex items-center gap-1">
                      {/* CR-8613: the ticks beside each part number are Direct. */}
                      {col.column_id === 'part_number' && onToggleDirect && (
                        <span title="Tick a part to mark it Direct (same as the Parts List)">
                          <Star className="h-3.5 w-3.5 text-amber-500" aria-label="Direct" />
                        </span>
                      )}
                      {col.label}
                    </span>
                    {renderSortIcon(col.column_id)}
                  </div>
                </th>
              );
            })}
            {onProposeChange && (
              <th className="text-xs text-right font-medium px-3 py-2" style={{ minWidth: 130 }}>
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((item) => (
            <tr key={rowKey(item)} className="border-b hover:bg-muted/50">
              {columns.map((col) => {
                const rawValue = item[col.column_id as keyof StockPlanningItem];
                const value = getCellValue(item, col.column_id);
                const frozen = isFrozen(col.column_id);
                return (
                  <td
                    key={col.column_id}
                    className={`text-sm px-3 py-2 align-top whitespace-normal break-words ${getCellClassName(col.column_id, value)} ${
                      frozen ? 'sticky z-10 bg-background shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''
                    }`}
                    style={frozen ? {
                      left: getFrozenLeft(col.column_id),
                      minWidth: FROZEN_WIDTHS[col.column_id] ?? 120,
                      maxWidth: FROZEN_WIDTHS[col.column_id] ?? 120,
                    } : undefined}
                  >
                    {col.column_id === 'part_number' && renderDirectTick(item)}
                    {col.column_id === 'part_number' && item.is_direct_product && <DirectProductStar />}
                    {col.column_id === 'remarks'
                      ? <RemarkCell item={item} onSave={onSaveRemark} saving={isRemarkSaving?.(item)} />
                      : col.column_id === 'fulfilment_pct'
                      ? renderFulfilmentBar(String(rawValue ?? ''))
                      : col.column_id === 'part_number' && onPartNumberClick && value !== '-'
                        ? (
                          <button
                            type="button"
                            onClick={() => onPartNumberClick(String(item.part_number))}
                            className="text-primary font-medium hover:underline text-left"
                            title="View purchase orders for this part"
                          >
                            {value}
                          </button>
                        )
                        : col.column_id === 'ordered_by_supplier' && zeroOrderedWhenProduced
                          ? (
                            <span
                              className={orderedComplete(item) ? 'text-muted-foreground' : ''}
                              title={orderedComplete(item) ? 'Order complete — stock uploaded' : undefined}
                            >
                              {String(parseFloat(item.remaining_to_produce) || 0)}
                            </span>
                          )
                          : value}
                  </td>
                );
              })}
              {onProposeChange && (
                <td className="text-sm px-3 py-2 align-top text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onProposeChange(item)}
                      title="Propose qty change for this part"
                    >
                      <Edit3 className="h-4 w-4 mr-1.5" />
                      Propose
                    </Button>
                    {onViewHistory && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewHistory(item)}
                        title="View part change history"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
